import BlogCategoryDropdown from '../components/BlogCategoryDropdown';

const selectors = {
  content: '[data-blog-content]',
  grid: '[data-blog-grid]',
  count: '[data-blog-count]',
  nav: '[data-category-nav]',
  dropdown: '[data-blog-dropdown]',
  categoryLink: '[data-category-link]',
  loadMoreWrapper: '[data-load-more-wrapper]',
  loadMoreButton: '[data-load-more-wrapper] .button',
};

const classes = {
  loading: ['opacity-50', 'pointer-events-none'],
};

/**
 * Blog listing: AJAX category switching and load more.
 *
 * Categories are article tags on a single blog, so every view - "all" and each
 * tag - is a normal paginated `blog.articles` render. Both interactions are
 * plain Section Rendering fetches with no special casing.
 */
export default class MainBlog {
  constructor(section) {
    this.el = section.el;
    this.sectionId = section.id;

    this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onDropdownSelect = this.onDropdownSelect.bind(this);

    this.init();
    window.addEventListener('popstate', this.onPopState);
  }

  init() {
    this.content = this.el.querySelector(selectors.content);
    this.grid = this.el.querySelector(selectors.grid);
    this.wrapper = this.el.querySelector(selectors.loadMoreWrapper);
    this.nav = this.el.querySelector(selectors.nav);

    if (this.wrapper) {
      this.wrapper.addEventListener('click', this.onLoadMoreClick);
    }

    if (this.nav) {
      this.nav.addEventListener('click', this.onNavClick);
      this.initDropdown();
    }
  }

  initDropdown() {
    const root = this.el.querySelector(selectors.dropdown);

    if (root) {
      this.dropdown = new BlogCategoryDropdown(root, this.onDropdownSelect);
    }
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    const target = this.content || this.el;

    if (isLoading) {
      target.classList.add(...classes.loading);
    } else {
      target.classList.remove(...classes.loading);
    }
  }

  async fetchDocument(url) {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Blog request failed: ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  withSectionId(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}section_id=${this.sectionId}`;
  }

  // Category switching

  onNavClick(event) {
    const link = event.target.closest(selectors.categoryLink);

    // Dropdown options are handled by the dropdown component so it can close
    // itself and update its label first.
    if (!link || link.closest(selectors.dropdown)) {
      return;
    }

    event.preventDefault();
    this.switchCategory(link.dataset.blogUrl || link.getAttribute('href'));
  }

  onDropdownSelect(url) {
    this.switchCategory(url);
  }

  async switchCategory(url) {
    if (!url || this.isLoading) {
      return;
    }

    this.setLoading(true);

    try {
      const parsed = await this.fetchDocument(this.withSectionId(url));
      const nextContent = parsed.querySelector(selectors.content);

      if (!nextContent) {
        throw new Error('Blog content missing from response');
      }

      this.teardownListeners();
      this.content.innerHTML = nextContent.innerHTML;

      window.history.pushState({}, '', url);
      this.setLoading(false);
      this.init();
    } catch {
      window.location.href = url;
    }
  }

  // Load more

  async onLoadMoreClick(event) {
    const button = event.target.closest('.button');

    if (!button || this.isLoading) {
      return;
    }

    event.preventDefault();

    const url = button.getAttribute('href');

    if (!url) {
      return;
    }

    this.isLoading = true;
    button.classList.add(...classes.loading);
    button.setAttribute('aria-busy', 'true');

    try {
      const parsed = await this.fetchDocument(this.withSectionId(url));

      this.appendArticles(parsed);
      this.updateCount(parsed);
      this.updateButton(parsed, button);
    } catch {
      // Fall back to a normal page navigation so the user is never stuck.
      window.location.href = url;
      return;
    } finally {
      this.isLoading = false;
      button.classList.remove(...classes.loading);
      button.removeAttribute('aria-busy');
    }
  }

  appendArticles(parsed) {
    const newGrid = parsed.querySelector(selectors.grid);

    if (newGrid) {
      this.grid.append(...newGrid.children);
    }
  }

  updateCount(parsed) {
    const newCount = parsed.querySelector(selectors.count);
    const currentCount = this.el.querySelector(selectors.count);

    if (newCount && currentCount) {
      currentCount.textContent = newCount.textContent;
    }
  }

  updateButton(parsed, button) {
    const newButton = parsed.querySelector(selectors.loadMoreButton);

    if (newButton) {
      button.setAttribute('href', newButton.getAttribute('href'));
      return;
    }

    this.removeLoadMore();
  }

  removeLoadMore() {
    if (!this.wrapper) {
      return;
    }

    this.wrapper.removeEventListener('click', this.onLoadMoreClick);
    this.wrapper.remove();
    this.wrapper = null;
  }

  onPopState() {
    window.location.reload();
  }

  teardownListeners() {
    if (this.wrapper) {
      this.wrapper.removeEventListener('click', this.onLoadMoreClick);
    }

    if (this.nav) {
      this.nav.removeEventListener('click', this.onNavClick);
    }

    if (this.dropdown) {
      this.dropdown.destroy();
      this.dropdown = null;
    }
  }

  onSectionUnload() {
    this.teardownListeners();
    window.removeEventListener('popstate', this.onPopState);
  }
}
